/**
 * PaymentsTable — таблица журнала (десктоп) + «⋯»-меню строки (открыть
 * клиента / детали / отметить возврат — только для успешных) и футер-пагинация
 * (стаб под мок). Клик по действию возврата уходит наружу.
 */

import {
  ChevronLeft,
  ChevronRight,
  FileText,
  MoreVertical,
  RotateCcw,
  UserRound,
} from 'lucide-react';

import type {PaymentRow} from '@services/payments';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@components/ui/DropdownMenu';
import {formatNumber} from '@utils/format';
import {ClientTypeIcon} from '@pages/Clients/ClientTypeIcon';

import {amountClass, fmtRowDate, fmtSignedSom} from '../../Payments.utils';
import {PaymentStatusPill} from '../../PaymentStatusPill';

const TH = 'px-4 py-[11px] text-[11px] font-semibold uppercase tracking-[.04em] text-skr-label';

type PaymentsTableProps = {
  rows: PaymentRow[];
  totalCount: number;
  onDetail: (row: PaymentRow) => void;
  onOpenClient: (row: PaymentRow) => void;
  onRefund: (row: PaymentRow) => void;
};

export const PaymentsTable = ({
  rows,
  totalCount,
  onDetail,
  onOpenClient,
  onRefund,
}: PaymentsTableProps) => (
  <div className="overflow-hidden rounded-[14px] border border-skr-line bg-card shadow-[0_1px_2px_rgba(20,25,40,.03)]">
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] border-collapse text-[13.5px]">
        <thead>
          <tr className="border-b border-skr-line2 bg-skr-head">
            <th className={`text-left ${TH}`}>Дата</th>
            <th className={`text-left ${TH}`}>Клиент</th>
            <th className={`text-left ${TH}`}>Тип</th>
            <th className={`text-left ${TH}`}>За период</th>
            <th className={`text-right ${TH}`}>Сумма, сом</th>
            <th className={`text-left ${TH}`}>Статус</th>
            <th className="w-11" />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-skr-hairline hover:bg-skr-head">
              <td className="whitespace-nowrap px-4 py-3 font-mono text-skr-ink2">
                {fmtRowDate(r.date)}
              </td>
              <td className="px-4 py-3">
                <div className="flex items-center gap-2.5">
                  <ClientTypeIcon type={r.clientType} size={32} />
                  <span className="font-semibold">{r.client}</span>
                </div>
              </td>
              <td className="px-4 py-3 text-skr-ink2">{r.type}</td>
              <td className="px-4 py-3 font-mono text-[13px] text-muted-foreground">{r.period}</td>
              <td
                className={`whitespace-nowrap px-4 py-3 text-right font-mono font-semibold ${amountClass(r)}`}
              >
                {fmtSignedSom(r.amount)}
              </td>
              <td className="px-4 py-3">
                <PaymentStatusPill status={r.status} />
              </td>
              <td className="px-2 py-3">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    aria-label="Действия"
                    className="grid size-[30px] place-items-center rounded-md text-skr-faint outline-none hover:bg-muted hover:text-muted-foreground"
                  >
                    <MoreVertical className="size-[17px]" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-[210px]">
                    <DropdownMenuItem onClick={() => onOpenClient(r)}>
                      <UserRound />
                      Открыть клиента
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onDetail(r)}>
                      <FileText />
                      Детали транзакции
                    </DropdownMenuItem>
                    {r.status === 'success' && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-skr-warn focus:text-skr-warn"
                          onClick={() => onRefund(r)}
                        >
                          <RotateCcw />
                          Отметить возврат
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={7} className="px-4 py-12 text-center text-[13px] text-muted-foreground">
                Платежей за период нет — журнал наполнится с запуском биллинга
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>

    <div className="flex items-center justify-between border-t border-skr-line2 px-[18px] py-3.5">
      <div className="text-[13px] text-muted-foreground">
        Показано <span className="font-mono text-skr-ink2">{rows.length}</span> из{' '}
        <span className="font-mono text-skr-ink2">{formatNumber(totalCount)}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          disabled
          aria-label="Назад"
          className="grid size-[34px] place-items-center rounded-[9px] border border-input text-skr-faint disabled:opacity-60"
        >
          <ChevronLeft className="size-4" strokeWidth={2} />
        </button>
        <button
          aria-label="Вперёд"
          className="grid size-[34px] place-items-center rounded-[9px] border border-input text-skr-ink2 hover:bg-muted"
        >
          <ChevronRight className="size-4" strokeWidth={2} />
        </button>
      </div>
    </div>
  </div>
);
